import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useDebounce } from 'usehooks-ts';
import { useQuerySearchParams, useUpdateQueryParams } from 'hooks/routes';
import { Parameters } from 'constants/queryParameters';
import { CloseIcon_24, SearchIcon_16 } from 'assets/icons';

import styles from './SearchBar.module.css';

type SearchBarProps = {
  isDisabled?: boolean;
};

const SearchBar = ({ isDisabled = false }: SearchBarProps): JSX.Element => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useQuerySearchParams<string>(Parameters.search);
  const [isTextEntered, setIsTextEntered] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchTerm = useDebounce(searchInput, 500);
  const updateQueryParams = useUpdateQueryParams();

  const clearSearch = () => {
    setSearchInput('');
    inputRef.current?.focus();
    updateQueryParams({ page: 1, search: undefined });
  };

  const handleBlur = () => {
    setIsTextEntered(false);
  };

  useEffect(() => {
    if (searchInput === '') {
      const handler = setTimeout(() => {
        setSearchInput('');
        updateQueryParams({ page: 1, search: undefined });
      }, 500);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [searchInput]);

  useEffect(() => {
    if (searchInput !== '' && searchInput !== search) {
      setSearch(debouncedSearchTerm);
      updateQueryParams({ page: 1, search: debouncedSearchTerm });
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    search !== undefined && setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (isTextEntered && searchInput !== '' && !isDisabled) {
      inputRef.current?.focus();
    }
  }, [isTextEntered, searchInput, isDisabled]);

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearSearch();
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [clearSearch]);

  return (
    <div className={styles.searchWrapper}>
      <input
        type={'search'}
        placeholder={'Search'}
        className={styles.search}
        value={searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
          setIsTextEntered(true);
        }}
        onBlur={handleBlur}
        ref={inputRef}
        disabled={isDisabled}
        maxLength={256}
      />
      <span className={cn(styles.searchIcon, isDisabled && styles.searchIconDisabled)}>
        <SearchIcon_16 height={'24px'} width={'24px'} />
      </span>
      {searchInput && (
        <span className={styles.closeIcon} onClick={clearSearch}>
          <CloseIcon_24 />
        </span>
      )}
    </div>
  );
};

export default SearchBar;
